import { useState } from 'react';
import { Button, Icons, Spinner } from '../../components/ui.tsx';
import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { pluralise } from '../../lib/display.ts';
import { useDownloads } from './useDownload.ts';
import { useDriveClipboard } from './DriveClipboard.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { can } from '@aq/shared';

/**
 * What you can do with a selection.
 *
 * Appears only when something is ticked, in place of the ordinary header, so
 * the toolbar is never a row of buttons that do nothing. The count is stated
 * plainly because bulk actions are the ones where acting on more than you
 * meant to is expensive.
 */
export function SelectionBar({
  count,
  payload,
  onClear,
  onChanged,
  folderId,
}: {
  count: number;
  payload: {
    folderIds: string[];
    fileIds: string[];
    spreadsheetIds: string[];
    documentIds: string[];
  };
  onClear: () => void;
  onChanged: () => void;
  /** Where a new archive is filed: the folder being looked at. */
  folderId: string | null;
}) {
  const dialog = useDialog();
  const toast = useToast();
  const { user } = useAuth();

  /*
   * The same two rights the row menu checks, for the same reason: a button
   * that is always there and sometimes refuses teaches people to click it and
   * read the red box.
   */
  const mayDelete = Boolean(user && can(user.role, 'file:delete'));
  const mayDownload = Boolean(user && can(user.role, 'file:download'));
  const mayWrite = Boolean(user && can(user.role, 'file:write'));
  const downloads = useDownloads();
  const clipboard = useDriveClipboard();
  const [busy, setBusy] = useState<'trash' | 'download' | 'compress' | null>(null);

  async function trash() {
    const folders = payload.folderIds.length;
    const confirmed = await dialog.confirm({
      title: `Move ${pluralise(count, 'item')} to the trash?`,
      message: 'Nothing is destroyed — trashed items can be put back.',
      details: folders > 0 ? [`${pluralise(folders, 'folder')}, and everything inside`] : [],
      confirmLabel: 'Move to trash',
      tone: 'danger',
    });
    if (!confirmed) return;

    setBusy('trash');
    try {
      await api.post('/api/drive/bulk/trash', payload);
      toast.success(`${pluralise(count, 'item')} moved to the trash`);
      onClear();
      onChanged();
    } catch (caught) {
      toast.error(
        'Could not move those to the trash',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    } finally {
      setBusy(null);
    }
  }

  /**
   * The same archive, kept in the Drive instead of sent down.
   *
   * A supplier wants one file rather than forty, and the way to get one was to
   * download the zip and upload it again. It is filed in the folder being
   * looked at, under the name the download would have used, and it goes
   * through the ordinary upload — so it is scanned, and a name already taken
   * asks the same three questions as any other upload.
   */
  async function compress() {
    setBusy('compress');
    try {
      const made = await api.post<{ name: string }>('/api/drive/bulk/compress', {
        ...payload,
        folderId,
        onConflict: 'rename',
      });
      toast.success(`"${made.name}" created`, 'The originals are untouched.');
      onClear();
      onChanged();
    } catch (caught) {
      toast.error(
        'Could not compress those',
        caught instanceof Error ? caught.message : 'Please try again.',
      );
    } finally {
      setBusy(null);
    }
  }

  /** Download the selection as one archive — folders included, with their contents. */
  async function download() {
    setBusy('download');
    try {
      await downloads.archive(payload, pluralise(count, 'item'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--blue-300)] bg-blue-50 px-4 py-2.5">
      <span className="text-body-sm font-medium text-blue-700">
        {pluralise(count, 'item')} selected
      </span>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <Button
          icon={<Icons.Copy />}
          onClick={() => {
            clipboard.copy(payload);
            toast.success(`${pluralise(count, 'item')} copied`, 'Open a folder and paste.');
          }}
          disabled={busy !== null}
        >
          Copy
        </Button>
        <Button
          icon={<Icons.Scissors />}
          onClick={() => {
            clipboard.cut(payload);
            toast.success(
              `${pluralise(count, 'item')} cut`,
              'Open a folder and paste to move them.',
            );
          }}
          disabled={busy !== null}
        >
          Cut
        </Button>
        {mayDownload ? (
          <Button
            icon={<Icons.Download />}
            onClick={() => void download()}
            disabled={busy !== null}
          >
            {busy === 'download' ? <Spinner label="Preparing" /> : 'Download as zip'}
          </Button>
        ) : null}
        {mayWrite ? (
          <Button icon={<Icons.Archive />} onClick={() => void compress()} disabled={busy !== null}>
            {busy === 'compress' ? <Spinner label="Compressing" /> : 'Compress here'}
          </Button>
        ) : null}
        {mayDelete ? (
          <Button variant="danger" onClick={() => void trash()} disabled={busy !== null}>
            {busy === 'trash' ? <Spinner label="Moving" /> : 'Move to trash'}
          </Button>
        ) : null}
        <button
          type="button"
          onClick={onClear}
          className="rounded-md px-2 py-1 text-body-sm text-blue-700 transition-fast hover:bg-blue-100"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

/** The tick box on a row, and in the header. */
export function SelectBox({
  checked,
  indeterminate = false,
  label,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onChange: () => void;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      aria-label={label}
      // Indeterminate is not an attribute — it can only be set on the node.
      ref={(node) => {
        if (node) node.indeterminate = indeterminate && !checked;
      }}
      onChange={onChange}
      onClick={(event) => event.stopPropagation()}
      className="h-4 w-4 cursor-pointer accent-[var(--blue-600)]"
    />
  );
}
